/**
 * Invoice Generator Utility
 * 
 * A reusable utility for generating PDF invoices with customizable content.
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import happeningsLogo from '../assets/happeings_logo.png';

/**
 * Generate a PDF invoice
 * 
 * @param {Object} invoiceData - The invoice data
 * @param {string} invoiceData.title - Title to display on the invoice
 * @param {string} invoiceData.recipient - Name of the invoice recipient
 * @param {Array} invoiceData.items - Line items for the invoice
 * @param {string} invoiceData.items[].name - Description of the item
 * @param {number} invoiceData.items[].amount - Amount for the item
 * @param {string} invoiceData.items[].type - 'positive' or 'negative'
 * @param {number} invoiceData.total - Total amount for the invoice
 * @param {Object} [options] - Additional options for invoice customization
 * @param {string} [options.companyName='Happenings'] - Company name
 * @param {Object} [options.paymentInfo] - Payment information
 * @param {string} [options.thankyouMessage] - Thank you message
 * @param {string} [options.fileName] - Custom filename for the PDF
 * @returns {Promise} Promise that resolves when PDF is generated and downloaded
 */
export const generateInvoice = (invoiceData, options = {}) => {
    return new Promise((resolve, reject) => {
        try {
            // Set default options
            const defaultOptions = {
                companyName: 'Happenings',
                paymentInfo: {
                    accountName: 'Happenings, Inc.',
                    accountNumber: '123456789',
                    routingNumber: '987654321'
                },
                thankyouMessage: `Thank you for your ${invoiceData.title?.toLowerCase() || 'business'}!`,
                fileName: `Invoice-${Date.now().toString().substring(8)}.pdf`
            };
            
            // Merge default options with provided options
            const mergedOptions = { ...defaultOptions, ...options };
            
            // Create a new PDF document
            const doc = new jsPDF();
            
            // Load and add company logo
            const addLogoToInvoice = () => {
                try {
                    // Add the logo with proper positioning
                    const img = new Image();
                    img.src = happeningsLogo;
                    
                    img.onload = function() {
                        // Calculate height to maintain aspect ratio (max width 40)
                        const imgWidth = 40;
                        const imgHeight = (img.height * imgWidth) / img.width;
                        
                        // Add logo at top left
                        doc.addImage(img, 'PNG', 20, 10, imgWidth, imgHeight);
                        
                        // Continue generating the rest of the invoice after logo is added
                        generateInvoiceContent();
                    };
                    
                    // In case the image fails to load
                    img.onerror = function() {
                        console.warn('Logo image failed to load. Continuing without logo.');
                        generateInvoiceContent();
                    };
                } catch (error) {
                    console.warn('Error adding logo:', error);
                    generateInvoiceContent();
                }
            };
            
            // Generate the content of the invoice
            const generateInvoiceContent = () => {
                // Add company name beside or below logo
                doc.setFontSize(22);
                doc.setTextColor(41, 128, 185); // Blue color
                doc.text(mergedOptions.companyName, 70, 25);
                
                // Add invoice header
                doc.setFontSize(18);
                doc.setTextColor(44, 62, 80); // Dark blue/gray
                doc.text('INVOICE', 20, 45);
                
                // Add invoice metadata
                doc.setFontSize(10);
                doc.setTextColor(100, 100, 100);
                const invoiceNumber = `INV-${Date.now().toString().substring(8)}`;
                const invoiceDate = new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                });
                
                doc.text(`Invoice Number: ${invoiceNumber}`, 20, 55);
                doc.text(`Invoice Date: ${invoiceDate}`, 20, 61);
                doc.text(`Due Date: ${invoiceDate}`, 20, 67);
                
                // Add horizontal line
                doc.setDrawColor(220, 220, 220);
                doc.line(20, 71, 190, 71);
                
                // Add recipient information
                doc.setFontSize(14);
                doc.setTextColor(44, 62, 80);
                doc.text(invoiceData.title?.toUpperCase() || 'RECIPIENT', 20, 81);
                doc.setFontSize(12);
                doc.text(invoiceData.recipient || '', 20, 88);
                
                // Create table for line items
                const tableColumn = ["Item", "Amount"];
                const tableRows = [];
                
                // Add rows from invoice data
                invoiceData.items.forEach(item => {
                    const amountText = `${item.type === 'positive' ? '+' : '-'}$${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    tableRows.push([item.name, amountText]);
                });
                
                // Add total row
                const totalLabel = invoiceData.totalLabel || 'Total Amount';
                tableRows.push([
                    totalLabel, 
                    `${invoiceData.total >= 0 ? '+' : ''}$${invoiceData.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                ]);
                
                // Generate the table
                const startY = 98;
                autoTable(doc, {
                    head: [tableColumn],
                    body: tableRows,
                    startY: startY,
                    styles: { 
                        fontSize: 12,
                        cellPadding: 6,
                        overflow: 'linebreak',
                        halign: 'left' 
                    },
                    columnStyles: {
                        0: { cellWidth: 'auto' },
                        1: { cellWidth: 50, halign: 'right' }
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold',
                    },
                    alternateRowStyles: { fillColor: [245, 245, 245] },
                    // Format items with colors based on positive/negative values
                    didParseCell: function(data) {
                        if (data.column.index === 1 && data.row.index < invoiceData.items.length) {
                            const item = invoiceData.items[data.row.index];
                            if (item.type === 'positive') {
                                data.cell.styles.textColor = [46, 204, 113]; // Green for positive
                            } else {
                                data.cell.styles.textColor = [231, 76, 60]; // Red for negative
                            }
                        }
                        
                        // Style the total row
                        if (data.row.index === tableRows.length - 1) {
                            data.cell.styles.fontStyle = 'bold';
                            data.cell.styles.fillColor = [236, 240, 241];
                            
                            // Color based on total value
                            if (data.column.index === 1) {
                                data.cell.styles.textColor = invoiceData.total >= 0 ? 
                                    [46, 204, 113] :  // Green for positive
                                    [231, 76, 60];    // Red for negative
                            }
                        }
                    }
                });
                
                // Get the y position after the table
                let finalY;
                try {
                    // Different versions of the library may store the finalY in different locations
                    if (doc.lastAutoTable) {
                        finalY = doc.lastAutoTable.finalY;
                    } else if (doc.previousAutoTable) {
                        finalY = doc.previousAutoTable.finalY;
                    } else {
                        // If we can't get the final Y position, estimate it based on content
                        finalY = startY + 10 + (tableRows.length * 12);
                    }
                } catch (error) {
                    // Fallback value if there's an error
                    finalY = startY + 10 + (tableRows.length * 12);
                }
                
                // Add payment information section
                doc.setFontSize(11);
                doc.setTextColor(44, 62, 80);
                doc.text('Payment Information', 20, finalY + 20);
                doc.setFontSize(10);
                doc.setTextColor(100, 100, 100);
                doc.text(`Please make payment to ${mergedOptions.paymentInfo.accountName}`, 20, finalY + 28);
                doc.text(`Account #: ${mergedOptions.paymentInfo.accountNumber}`, 20, finalY + 34);
                doc.text(`Routing #: ${mergedOptions.paymentInfo.routingNumber}`, 20, finalY + 40);
                
                // Add thank you note
                const pageHeight = doc.internal.pageSize.height;
                doc.setFontSize(10);
                doc.setTextColor(100, 100, 100);
                doc.text(mergedOptions.thankyouMessage, 20, pageHeight - 20);
                
                // Save the PDF with the specified or generated filename
                doc.save(mergedOptions.fileName);
                
                // Resolve the promise when done
                resolve({ success: true, invoiceNumber });
            };
            
            // Start the process by adding the logo
            addLogoToInvoice();
        } catch (error) {
            console.error('Error generating invoice:', error);
            reject(error);
        }
    });
};

/**
 * Generate a sponsor invoice specifically
 * 
 * @param {Object} sponsorData - Data about the sponsor
 * @param {string} sponsorData.name - Sponsor name
 * @param {Array} sponsorData.items - Line items for the invoice
 * @param {number} sponsorData.total - Total amount
 * @returns {Promise} Promise that resolves when PDF is generated and downloaded
 */
export const generateSponsorInvoice = (sponsorData) => {
    return generateInvoice({
        title: 'Sponsor',
        recipient: sponsorData.name,
        items: sponsorData.items,
        total: sponsorData.total,
        totalLabel: 'Total Sponsorship Discount'
    }, {
        thankyouMessage: 'Thank you for your sponsorship!'
    });
};

export default {
    generateInvoice,
    generateSponsorInvoice
}; 